<script setup lang="ts">
import type { MovementGridRow } from "@/redistribution/models/MovementGridRow.ts";
import { saveAs } from "file-saver";
import moment from "moment";
import { storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import * as xlsx from "xlsx";
import StatusBanner from "../components/StatusBanner.vue";
import SummaryCard from "../components/SummaryCard.vue";
import SummaryCardGroup from "../components/SummaryCardGroup.vue";
import Tab from "../components/Tab.vue";
import TabGroup from "../components/TabGroup.vue";
import { renderTraceLogAsText } from "../features/dashboard/dashboardTraceMapper.ts";
import { showPoDetailPreviousValues } from "../features/home/featureFlags.ts";
import BeforeAfterGrid from "../features/results/BeforeAfterGrid.vue";
import {
  beforeAfterPoQty,
  formatBeforeAfterPoCell,
  isBeforeAfterPoCell,
} from "../features/results/beforeAfterPoCell.ts";
import BulkOrderDatesPanel from "../features/results/BulkOrderDatesPanel.vue";
import {
  downloadJsonFile,
  formatJsonForDownload,
} from "../features/results/downloadJson.ts";
import ItemMovementsGrid from "../features/results/ItemMovementsGrid.vue";
import PoDetailGrid from "../features/results/PoDetailGrid.vue";
import type { RedistributionResult } from "../redistribution/models/RedistributionResult.js";
import { useAppStore } from "../stores/app.ts";
import { useRedistributionStore } from "../stores/redistributionStore.ts";

const redistributionStore = useRedistributionStore();
const {
  beforeAfterGridRowData,
  beforeAfterView,
  itemMovementsGridRowData,
  lastCustomRequestBody,
  poDetailGridRowDatas,
  redistributionResult,
  updatedPOs,
  traceLog,
} = storeToRefs(redistributionStore);

const displayTraceLog = computed(() => {
  if (!traceLog.value) return;
  return renderTraceLogAsText(traceLog.value);
});

const router = useRouter();

const { clearResult } = redistributionStore;
const appStore = useAppStore();

// If no result data, redirect back
if (!redistributionResult.value) {
  void router.replace({ name: "search-select-pos" });
}

/* ── Computed data ── */

const r = computed<RedistributionResult | undefined>(
  () => redistributionResult.value
);

const totalPOs = computed(() => updatedPOs.value.length);
const totalSkus = computed(() =>
  updatedPOs.value.reduce((s, po) => s + po.countUniqueSkus, 0)
);
const totalItems = computed(() =>
  updatedPOs.value.reduce((s, po) => s + po.sumQuantities, 0)
);
const showCalculationDetails = computed(
  () => !!r.value?.traceLogBlobId || !!r.value?.traceLog || !!traceLog.value
);
const showTraceLogTab = computed(
  () =>
    !!redistributionResult.value?.traceLogBlobId ||
    !!redistributionResult.value?.traceLog ||
    !!traceLog.value
);
const demandWarnings = computed(() => r.value?.warnings ?? []);
const demandWarningMessages = computed(() =>
  demandWarnings.value.map((text) => ({
    type: "warning" as const,
    text,
  }))
);
watch(
  () => r.value,
  (result) => {
    if (!result) return;
    if (result.success) {
      toast.success("Redistribution completed successfully.");
      if (itemMovementsGridRowData.value.length === 0) {
        toast.info(
          "All SKUs on the selected POs are already healthy — no SKUs were moved."
        );
      }
    } else {
      toast.error(result.message);
    }
  },
  { immediate: true }
);

const startNew = (): void => {
  appStore.clearSelections();
  appStore.clearSearchParameters();
  clearResult();
  void router.push({ name: "search-select-pos" });
};

const backToSearch = (): void => {
  void router.push({ name: "search-select-pos" });
};
const seeCalculation = (): void => {
  void router.push({
    name: "dashboard-algorithm-trace-log",
    query: { blobId: r.value?.traceLogBlobId },
  });
};
const getBeforeAfterRowDataJson = (): Record<string, unknown>[] => {
  return structuredClone(beforeAfterGridRowData.value).map((row) => {
    const exported: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      exported[key] = isBeforeAfterPoCell(value)
        ? beforeAfterView.value === "details"
          ? formatBeforeAfterPoCell(value)
          : beforeAfterPoQty(value)
        : value;
    }
    return exported;
  });
};
const getMovementsRowDataJson = (): MovementGridRow[] => {
  const sc = structuredClone(itemMovementsGridRowData.value);
  return sc;
};
const getPoDetailRowDataJson = (
  poNumber: string
): Record<string, unknown>[] => {
  return (poDetailGridRowDatas.value[poNumber] ?? []).map((row) => {
    const exported: Record<string, unknown> = {
      SKU: row.sku,
      Collection: row.collection,
      Description: row.description,
      Color: row.color,
      "Quantity Available": row.quantityAvailable,
      "Forecast Quantity Available": row.forecastQuantityAvailable,
      "Adjusted Forecast": row.adjustedForecast,
      "Daily Rate of Sale": row.dailyRateOfSale,
    };
    if (showPoDetailPreviousValues) {
      exported["Previous Quantity"] = row.previousQuantity;
    }
    exported.Quantity = row.quantity;
    exported.Difference = row.difference;
    if (showPoDetailPreviousValues) {
      exported["Previous Weeks of Stock"] = row.previousWeeksOfStock;
    }
    exported["Weeks of Stock"] = row.weeksOfStock;
    return exported;
  });
};
const setColumnWidths = (
  worksheet: xlsx.WorkSheet,
  jsonData: Record<string, unknown>[],
  getWidth: (key: string) => number = () => 20
): void => {
  if (!worksheet["!cols"]) {
    worksheet["!cols"] = [];
    for (const key of Object.keys(jsonData[0] ?? {})) {
      worksheet["!cols"].push({ wch: getWidth(key) });
    }
  }
};
const formatPOColumn = (key: string): string => {
  const parts = key.split("_");
  if (parts.length === 2) {
    const [poNumber, desc] = parts;
    switch (desc) {
      case "before": {
        return `${String(poNumber)} Before`;
      }
      case "after": {
        return `${String(poNumber)} After`;
      }
      default: {
        return key;
      }
    }
  } else {
    return key;
  }
};
const exportToExcel = (): void => {
  const workbook = xlsx.utils.book_new();
  // Before & After Sheet
  const beforeAfterGridRowDataJson = getBeforeAfterRowDataJson();
  const beforeAfterSheetData =
    beforeAfterGridRowDataJson.length > 0
      ? beforeAfterGridRowDataJson
      : [{ "Before & After": "No Changes Needed" }];
  const beforeAfterSheetHeaderKeys = Object.keys(beforeAfterSheetData[0] ?? {});
  const beforeAfterSheetHeader = beforeAfterSheetHeaderKeys.map((key) => {
    switch (key) {
      case "sku": {
        return "SKU";
      }
      case "description": {
        return "Description";
      }
      case "daysToStockout": {
        return "Days to Stockout";
      }
      case "totalBefore": {
        return "Total Before";
      }
      case "totalAfter": {
        return "Total After";
      }
      default: {
        return formatPOColumn(key);
      }
    }
  });
  const beforeAfterSheet = xlsx.utils.json_to_sheet([], {
    header: beforeAfterSheetHeader,
  });
  xlsx.utils.sheet_add_json(beforeAfterSheet, beforeAfterSheetData, {
    origin: "A2",
    skipHeader: true,
  });
  setColumnWidths(beforeAfterSheet, beforeAfterSheetData);
  xlsx.utils.book_append_sheet(workbook, beforeAfterSheet, "Before & After");

  // Item Movements Sheet
  const movementsGridRowDataJson = getMovementsRowDataJson();
  const movementsSheetData =
    movementsGridRowDataJson.length > 0
      ? movementsGridRowDataJson
      : [{ "Item Movements": "No Item Movements to Display" }];
  const movementsSheetHeader = Object.keys(movementsSheetData[0] ?? {}).map(
    (key) => {
      // Convert camelCase to Title Case for headers
      const result = key
        .replaceAll(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .replace("_", " ");
      return result;
    }
  );
  const movementsSheet = xlsx.utils.json_to_sheet([], {
    header: movementsSheetHeader,
  });
  xlsx.utils.sheet_add_json(movementsSheet, movementsSheetData, {
    origin: "A2",
    skipHeader: true,
  });
  setColumnWidths(
    movementsSheet,
    movementsSheetData as Record<string, unknown>[],
    (key) => {
      switch (key) {
        case "description": {
          return 50;
        }
        case "from":
        case "to": {
          return 30;
        }
        default: {
          return 20;
        }
      }
    }
  );
  xlsx.utils.book_append_sheet(workbook, movementsSheet, "Item Movements");

  // PO Detail Sheets
  const poDetailHeader = [
    "SKU",
    "Collection",
    "Description",
    "Color",
    "Quantity Available",
    "Forecast Quantity Available",
    "Adjusted Forecast",
    "Daily Rate of Sale",
    ...(showPoDetailPreviousValues ? ["Previous Quantity"] : []),
    "Quantity",
    "Difference",
    ...(showPoDetailPreviousValues ? ["Previous Weeks of Stock"] : []),
    "Weeks of Stock",
  ];
  const poDetailSheet = xlsx.utils.json_to_sheet([], {
    header: poDetailHeader,
    skipHeader: true,
  });
  let row = 1;
  for (const [poNumber] of Object.entries(poDetailGridRowDatas.value)) {
    const poDetailGridRowDataJson = getPoDetailRowDataJson(poNumber);
    xlsx.utils.sheet_add_aoa(poDetailSheet, [[`PO Detail for ${poNumber}`]], {
      origin: `A${String(row)}`,
    });
    if (poDetailGridRowDataJson.length === 0) {
      xlsx.utils.sheet_add_aoa(poDetailSheet, [["No changes needed"]], {
        origin: `A${String(row + 1)}`,
      });
      row += 3;
      continue;
    }
    xlsx.utils.sheet_add_aoa(poDetailSheet, [poDetailHeader], {
      origin: `A${String(row + 1)}`,
    });
    xlsx.utils.sheet_add_json(poDetailSheet, poDetailGridRowDataJson, {
      origin: `A${String(row + 2)}`,
      skipHeader: true,
    });
    row += poDetailGridRowDataJson.length + 3;
  }
  const poDetailSheetMockJsonFirstObject: Record<string, unknown> = {};
  for (const header of poDetailHeader) {
    poDetailSheetMockJsonFirstObject[header] = "";
  }
  const poDetailSheetMockJson: Record<string, unknown>[] = [
    poDetailSheetMockJsonFirstObject,
  ];
  setColumnWidths(poDetailSheet, poDetailSheetMockJson, (key) => {
    switch (key) {
      case "Description": {
        return 50;
      }
      default: {
        return 25;
      }
    }
  });
  xlsx.utils.book_append_sheet(workbook, poDetailSheet, "PO Detail");

  // Write to file
  const now = moment().format("YYYY-MM-DD_HH-mm-ss");
  const filename = `redistribution_result_${now}.xlsx`;
  const wbout = xlsx.write(workbook, {
    bookType: "xlsx",
    type: "array",
  }) as ArrayBuffer;
  const blob: Blob = new Blob([wbout], { type: "application/octet-stream" });

  saveAs(blob, filename);
};

const downloadTimestamp = (): string => moment().format("YYYY-MM-DD_HH-mm-ss");

const downloadRequestJson = (): void => {
  const requestBody = lastCustomRequestBody.value;
  if (!requestBody) {
    toast.warning("Request JSON is not available for this run.");
    return;
  }
  downloadJsonFile(
    `redistribution_request_${downloadTimestamp()}.json`,
    formatJsonForDownload(requestBody)
  );
};

const downloadResponseJson = (): void => {
  if (!r.value) {
    toast.warning("Response JSON is not available for this run.");
    return;
  }
  downloadJsonFile(
    `redistribution_response_${downloadTimestamp()}.json`,
    formatJsonForDownload(r.value)
  );
};
</script>

<template>
  <main v-if="r" class="page-main-container results-page-main overflow-hidden">
    <div
      class="flex min-h-0 flex-1 flex-col rounded-lg bg-white shadow-[0_2px_8px_rgb(0_0_0/8%)]"
    >
      <div class="panel-card-header shrink-0">
        <h2>✅ Redistribution Results</h2>
        <div class="flex flex-wrap justify-end gap-2">
          <button class="panel-secondary-button" @click="backToSearch">
            ← Modify Previous Selections
          </button>
          <button class="panel-secondary-button" @click="startNew">
            ← New Redistribution
          </button>
          <button class="panel-secondary-button" @click="exportToExcel">
            ↑ Export to Excel
          </button>
          <button
            class="panel-secondary-button"
            data-testid="download-request-json"
            :disabled="!lastCustomRequestBody"
            @click="downloadRequestJson"
          >
            ↓ Request JSON
          </button>
          <button
            class="panel-secondary-button"
            data-testid="download-response-json"
            @click="downloadResponseJson"
          >
            ↓ Response JSON
          </button>
        </div>
      </div>
      <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div class="shrink-0 px-6 pt-6 pb-4" @click.stop>
          <div
            v-if="r.success"
            data-testid="results-toast-region"
            class="sr-only"
            role="status"
            aria-live="polite"
          >
            Redistribution completed successfully.
          </div>

          <!-- Result Banner (failure state only; success is handled via toast) -->
          <div v-if="!r.success" role="alert" aria-live="assertive">
            <StatusBanner class="shrink-0" variant="error">
              {{ r.message ?? "Redistribution failed." }}
            </StatusBanner>
          </div>

          <div
            v-if="r.success && demandWarningMessages.length > 0"
            role="status"
            aria-live="polite"
            class="mt-4"
          >
            <StatusBanner
              class="shrink-0"
              variant="warning"
              :messages="demandWarningMessages"
            />
          </div>

          <!-- Summary KPI Cards -->
          <SummaryCardGroup class="mt-4 shrink-0">
            <SummaryCard
              variant="info"
              label="Purchase Orders"
              :value="totalPOs"
              detail="POs included in redistribution"
            />
            <SummaryCard
              variant="info"
              label="Total SKUs"
              :value="totalSkus"
              detail="Across all updated POs"
            />
            <SummaryCard
              variant="info"
              label="Total Items"
              :value="totalItems"
              detail="Sum of all SKU quantities"
            />
          </SummaryCardGroup>
        </div>
        <div class="flex min-h-0 flex-1 flex-col px-6 pb-6" @click.stop>
          <div class="mb-3 flex items-start justify-between gap-4">
            <BulkOrderDatesPanel />
            <button
              v-if="showCalculationDetails"
              class="panel-secondary-button shrink-0"
              @click="seeCalculation"
            >
              See Calculation Details
            </button>
          </div>
          <!-- Tabs -->
          <TabGroup default-tab="before" :fill-container="true">
            <Tab name="before" label="Before &amp; After">
              <BeforeAfterGrid />
            </Tab>
            <Tab name="movements" label="Item Movements">
              <ItemMovementsGrid />
            </Tab>
            <Tab name="poDetail" label="PO Detail">
              <PoDetailGrid />
            </Tab>
            <Tab v-if="showTraceLogTab" name="traceLog" label="Trace Log">
              <div class="min-h-0 flex-1 overflow-auto p-4">
                <div v-if="!displayTraceLog" class="text-sm text-slate-500">
                  Loading trace log…
                </div>
                <pre
                  v-else
                  class="min-h-0 flex-1 overflow-auto rounded border border-slate-200 bg-slate-50 p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap text-slate-800"
                  >{{ displayTraceLog }}</pre
                >
              </div>
            </Tab>
          </TabGroup>
        </div>
      </div>
    </div>
  </main>
</template>
